import { useCallback } from 'react';
import { isEmpty } from 'lodash-es';
import { useGeneralRoutes } from '@namespace/cms';
import { commandsMap, LIMIT_TYPES } from '@namespace/responsible-gambling';
import { useHistory } from '@namespace/router';
import { socketSend } from '@namespace/socket';
import { getAppleSession } from '../../api/getAppleSession';
import { paymentsActions } from '../../store';
import { ErrorWithErrorCode } from '../../types/errorWithCode';
import { session } from './useApplePaySession';

const { DEPOSIT } = paymentsActions;

type OnDepositParams = Record<string, unknown> & {
  amount?: string | number;
};

export const useNativeDeposit = () => {
  const { successfulDeposit, unsuccessfulDeposit } = useGeneralRoutes();
  const { push } = useHistory();

  const checkIfUserSelfExcluded = useCallback(async () => {
    const { data } = await socketSend({
      cmd: 'do_proto_v1',
      data: {
        query: {
          [commandsMap[LIMIT_TYPES.SELF_EXCLUSION]]: {}
        }
      }
    });

    return !isEmpty(data?.[commandsMap[LIMIT_TYPES.SELF_EXCLUSION]]);
  }, []);

  const onDeposit = useCallback(
    async (data: OnDepositParams) => {
      const isSelfExcludedUser = await checkIfUserSelfExcluded();

      return new Promise((resolve, reject) => {
        if (isSelfExcludedUser) {
          const error: ErrorWithErrorCode = new Error('');
          error.errorCode = 'accounting_error_1067';

          reject(error);
          return;
        }

        const onValidateMerchant = () =>
          getAppleSession()
            .then((merchantSession) =>
              session.completeMerchantValidation(merchantSession)
            )
            .catch((err) => reject(err));

        const onPaymentAuthorized = async (
          event = {} as ApplePayJS.ApplePayPaymentAuthorizedEvent
        ) => {
          const { payment = {} } = event;
          const { token = {} } = payment as ApplePayJS.ApplePayPayment;
          const { paymentData } = token as ApplePayJS.ApplePayPaymentToken;

          await DEPOSIT({
            ...data,
            applePayToken: paymentData
          })
            .then((response) => {
              session.completePayment({
                status: ApplePaySession.STATUS_SUCCESS
              });
              push(successfulDeposit);
              return resolve(response);
            })
            .catch((err) => {
              const { errorCode = '404' } = err;
              session.completePayment({
                status: ApplePaySession.STATUS_FAILURE
              });
              push(`${unsuccessfulDeposit}?code=${errorCode}`);
              return reject(err);
            });
        };

        session.onvalidatemerchant = onValidateMerchant;

        session.onpaymentauthorized = onPaymentAuthorized;

        session.oncancel = (event) => {
          console.error(event);
          reject();
        };

        session.begin();
      });
    },
    [checkIfUserSelfExcluded, push, successfulDeposit, unsuccessfulDeposit]
  );

  return {
    onDeposit
  };
};
